import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_HUSTLE,
  HUSTLE_BIO_MAX,
  HUSTLE_KINDS,
  formatPence,
  type HustleAction,
  type HustleBoardReply,
  type HustleContent,
  type HustleKind,
  type HustleProView,
  type HustleReply,
} from '@london-life/contracts';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { useCatalogue } from '../lib/content';
import { actionKey, londonDay, typedPence } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';
import { DecorScreen } from './decor-screen';
import { JobCard, StatsLine } from './job-card';
import { JobForm } from './job-form';

type Tab = 'board' | 'pros' | 'mine' | 'listme';
const TABS: { id: Tab; label: string }[] = [
  { id: 'board', label: '📋 Board' },
  { id: 'pros', label: '🙋🏾 Pros' },
  { id: 'mine', label: '🧰 Mine' },
  { id: 'listme', label: '✍🏾 List me' },
];
type Run = (work: () => Promise<HustleReply>) => Promise<boolean>;

// Hustle, as Lagos Life's 🧰 Hustle: players hire players. The Board (post a job, apply, hire), the pros listed
// for hire (offer them a job), your jobs (as the poster and as the worker), and listing yourself. The money is
// held when someone is hired and paid when the job is done.
export function HustleScreen(props: PhoneAppProps) {
  const hustle = useCatalogue()?.hustle ?? DEFAULT_HUSTLE;
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.hustle.mine(), props);
  const [tab, setTab] = useState<Tab>('board');
  // a decorate job's copy of the home, open over the app
  const [looking, setLooking] = useState<string | null>(null);
  const waiting = reply
    ? reply.working.filter((job) => job.status === 'offered').length +
      reply.mine.filter((job) => job.status === 'done' || (job.status === 'open' && job.applicants.length > 0)).length
    : 0;
  return (
    <PhoneAppFrame
      id="hustle"
      title="🧰 Hustle"
      className="hustle-screen"
      closeLabel="Close Hustle"
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Opening Hustle…'}
    >
      {reply && (
        <div className="business-detail">
          <div className="flights-tabs hustle-tabs" role="tablist" aria-label="Hustle">
            {TABS.map((item) => (
              <button
                type="button"
                key={item.id}
                role="tab"
                aria-selected={tab === item.id}
                className={tab === item.id ? 'active' : ''}
                onClick={() => setTab(item.id)}
              >
                {item.label}
                {item.id === 'mine' && waiting > 0 ? ` (${waiting})` : ''}
              </button>
            ))}
          </div>
          {tab === 'board' && <Board hustle={hustle} reply={reply} busy={busy} run={run} />}
          {tab === 'pros' && <Pros hustle={hustle} reply={reply} busy={busy} run={run} />}
          {tab === 'mine' && (
            <Mine hustle={hustle} reply={reply} busy={busy} run={run} onLook={props.life ? setLooking : undefined} />
          )}
          {tab === 'listme' && <ListMe hustle={hustle} reply={reply} busy={busy} run={run} />}
        </div>
      )}
      {looking && props.life && (
        <DecorScreen
          jobId={looking}
          life={props.life}
          dataSaver={props.dataSaver}
          onClose={() => setLooking(null)}
          onChanged={() => {
            void load();
            props.onWallet();
          }}
        />
      )}
    </PhoneAppFrame>
  );
}

// a kind to look at (or all of them)
function KindChips({
  hustle,
  value,
  onChange,
}: {
  hustle: HustleContent;
  value: HustleKind | null;
  onChange: (kind: HustleKind | null) => void;
}) {
  return (
    <div className="business-chips hustle-kinds" role="radiogroup" aria-label="Kind of job">
      <button
        type="button"
        role="radio"
        aria-checked={!value}
        className={!value ? 'active' : ''}
        onClick={() => onChange(null)}
      >
        All
      </button>
      {hustle.kinds
        .filter((item) => item.visible)
        .map((item) => (
          <button
            key={item.kind}
            type="button"
            role="radio"
            aria-checked={value === item.kind}
            className={value === item.kind ? 'active' : ''}
            onClick={() => onChange(item.kind)}
          >
            {item.emoji} {item.short}
          </button>
        ))}
    </div>
  );
}

// the Board: open posts (load more as you go), and posting your own
function Board({ hustle, reply, busy, run }: { hustle: HustleContent; reply: HustleReply; busy: boolean; run: Run }) {
  const [kind, setKind] = useState<HustleKind | null>(null);
  const [board, setBoard] = useState<HustleBoardReply | null>(null);
  const [posting, setPosting] = useState(false);
  const fetchBoard = useCallback(
    () =>
      api.hustle
        .board(kind ? { kind } : {})
        .then(setBoard)
        .catch(() => notify("Couldn't load the Board. Try again")),
    [kind],
  );
  useEffect(() => void fetchBoard(), [fetchBoard]);
  const more = () =>
    board?.next &&
    void api.hustle
      .board({ ...(kind ? { kind } : {}), before: board.next })
      .then((next) => setBoard({ jobs: [...board.jobs, ...next.jobs], next: next.next }))
      .catch(() => notify("Couldn't load more. Try again"));
  const act = (id: string, body: HustleAction) => void run(() => api.hustle.act(id, body)).then(() => fetchBoard());
  return (
    <>
      <button
        type="button"
        className="flights-terminal hustle-post"
        aria-expanded={posting}
        onClick={() => setPosting(!posting)}
      >
        ➕ Post a job…
      </button>
      {posting && (
        <JobForm
          hustle={hustle}
          reply={reply}
          kinds={[...HUSTLE_KINDS]}
          busy={busy}
          onSend={(body) =>
            void run(() => api.hustle.post(body, actionKey('hustle'))).then((done) => done && setPosting(false))
          }
        />
      )}
      <KindChips hustle={hustle} value={kind} onChange={setKind} />
      {!board && <p className="business-muted">Loading the Board…</p>}
      {board && board.jobs.length === 0 && <p className="business-muted">No jobs on the Board right now. Post one!</p>}
      <ul className="staff-list hustle-list" aria-label="The Board">
        {board?.jobs.map((job) => (
          <JobCard key={job.id} job={job} hustle={hustle} busy={busy} onAct={(body) => act(job.id, body)} />
        ))}
      </ul>
      {board?.next && (
        <button type="button" className="flights-terminal" onClick={more}>
          Load more jobs
        </button>
      )}
    </>
  );
}

// players listed for hire, and an offer to one of them
function Pros({ hustle, reply, busy, run }: { hustle: HustleContent; reply: HustleReply; busy: boolean; run: Run }) {
  const [kind, setKind] = useState<HustleKind | null>(null);
  const [pros, setPros] = useState<HustleProView[] | null>(null);
  const [offering, setOffering] = useState<string | null>(null);
  useEffect(() => {
    void api.hustle
      .pros(kind ? { kind } : {})
      .then((next) => setPros(next.pros))
      .catch(() => notify("Couldn't find pros. Try again"));
  }, [kind]);
  const short = (id: HustleKind) => hustle.kinds.find((item) => item.kind === id);
  return (
    <>
      <KindChips hustle={hustle} value={kind} onChange={setKind} />
      {!pros && <p className="business-muted">Finding pros…</p>}
      {pros && pros.length === 0 && (
        <p className="business-muted">
          {kind ? `No ${short(kind)?.short.toLowerCase()}s listed right now.` : 'Nobody is listed right now.'} Post a
          job on the Board instead.
        </p>
      )}
      <ul className="staff-list hustle-list" aria-label="Pros for hire">
        {pros?.map((pro) => (
          <li key={pro.lifeId}>
            <span aria-hidden="true">🙋🏾</span>
            <span>
              <strong>
                {pro.name} · {formatPence(pro.ratePence)}
              </strong>
              <StatsLine stats={pro.stats} />
              <p>{pro.bio}</p>
              <small>{pro.kinds.map((id) => `${short(id)?.emoji ?? ''} ${short(id)?.short ?? id}`).join(' · ')}</small>
              <button
                type="button"
                className="flights-terminal"
                aria-expanded={offering === pro.lifeId}
                onClick={() => setOffering(offering === pro.lifeId ? null : pro.lifeId)}
              >
                Offer {pro.name} a job…
              </button>
              {offering === pro.lifeId && (
                <JobForm
                  hustle={hustle}
                  reply={reply}
                  kinds={pro.kinds}
                  firstFee={pro.ratePence}
                  offerTo={pro.name}
                  busy={busy}
                  onSend={(body) =>
                    void run(() => api.hustle.offer({ ...body, lifeId: pro.lifeId }, actionKey('hustle'))).then(
                      (done) => done && setOffering(null),
                    )
                  }
                />
              )}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

// your jobs: the ones you posted or offered, and the ones you were offered or hired for
function Mine({
  hustle,
  reply,
  busy,
  run,
  onLook,
}: {
  hustle: HustleContent;
  reply: HustleReply;
  busy: boolean;
  run: Run;
  onLook?: (jobId: string) => void;
}) {
  const act = (id: string, body: HustleAction) => void run(() => api.hustle.act(id, body));
  return (
    <>
      <strong className="business-step-title">JOBS FOR YOU</strong>
      {reply.working.length === 0 && (
        <p className="business-muted">Apply on the Board, or list yourself for hire and clients will find you.</p>
      )}
      <ul className="staff-list hustle-list" aria-label="Jobs for you">
        {reply.working.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            hustle={hustle}
            busy={busy}
            onAct={(body) => act(job.id, body)}
            onLook={onLook && (() => onLook(job.id))}
          />
        ))}
      </ul>
      <strong className="business-step-title">JOBS YOU POSTED</strong>
      {reply.mine.length === 0 && <p className="business-muted">Post a job on the Board, or offer one to a pro.</p>}
      <ul className="staff-list hustle-list" aria-label="Jobs you posted">
        {reply.mine.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            hustle={hustle}
            busy={busy}
            onAct={(body) => act(job.id, body)}
            onLook={onLook && (() => onLook(job.id))}
          />
        ))}
      </ul>
      <p className="business-muted">
        When the worker marks a job done, pay them, or say it is not done yet once. It pays itself {hustle.payHours}{' '}
        hours after it was marked done. Calling it off before then gives you all the money back. A decorator works on a
        copy of your home and sends you a look: nothing changes in your home until you accept it.
      </p>
    </>
  );
}

// list yourself for hire: the kinds of job you do, your rate and a line about you
function ListMe({ hustle, reply, busy, run }: { hustle: HustleContent; reply: HustleReply; busy: boolean; run: Run }) {
  const [kinds, setKinds] = useState<HustleKind[]>(reply.listing?.kinds ?? []);
  const [rate, setRate] = useState(reply.listing ? String(reply.listing.ratePence / 100) : '');
  const [bio, setBio] = useState(reply.listing?.bio ?? '');
  const ratePence = typedPence(rate);
  const toggle = (id: HustleKind) =>
    setKinds((now) => (now.includes(id) ? now.filter((item) => item !== id) : [...now, id].slice(0, hustle.kindsMax)));
  return (
    <div className="hustle-form">
      <p className="business-muted">
        List the jobs you do and your rate: clients find you under Pros and offer you jobs straight away.
        {reply.listing ? ` You're listed until ${londonDay(reply.listing.listedUntil)}.` : ''}
      </p>
      <div className="business-chips" role="group" aria-label={`The jobs you do (up to ${hustle.kindsMax})`}>
        {hustle.kinds
          .filter((item) => item.visible)
          .map((item) => (
            <button
              key={item.kind}
              type="button"
              aria-pressed={kinds.includes(item.kind)}
              className={kinds.includes(item.kind) ? 'active' : ''}
              onClick={() => toggle(item.kind)}
            >
              {item.emoji} {item.short}
            </button>
          ))}
      </div>
      <label className="railco-field">
        <span>YOUR RATE (£)</span>
        <input inputMode="decimal" value={rate} placeholder="30" onChange={(event) => setRate(event.target.value)} />
      </label>
      <label className="railco-field">
        <span>A LINE ABOUT YOU</span>
        <input
          value={bio}
          maxLength={HUSTLE_BIO_MAX}
          placeholder="Afrobeats and amapiano all night"
          onChange={(event) => setBio(event.target.value)}
        />
        <small>
          {bio.trim().length}/{HUSTLE_BIO_MAX}
        </small>
      </label>
      <button
        type="button"
        className="city-card-go"
        disabled={busy || !kinds.length || !ratePence || !bio.trim()}
        onClick={() => void run(() => api.hustle.list({ kinds, ratePence, bio: bio.trim() }))}
      >
        {reply.listing ? 'Update my listing' : 'List me for hire'}
      </button>
      {reply.listing && (
        <button
          type="button"
          className="flights-terminal"
          disabled={busy}
          onClick={() => void run(() => api.hustle.unlist())}
        >
          Take me off the list
        </button>
      )}
    </div>
  );
}
